/**
 * Custom hook for Student Uniform handlers
 * FIXES CODE DUPLICATION: handleSave and handleSubmit shared 80% of their logic
 * This hook extracts the common logic into reusable functions
 */

import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import { saveUniform, submitUniform } from '../../../store/slices/uniformSlice';
import {
  filterModifiedData,
  validateUniformData,
  buildValidationErrorHtml
} from '../utils/uniformHelpers';

export const useUniformHandlers = () => {
  const dispatch = useDispatch();

  /**
   * CORE SHARED LOGIC: Prepare and execute save/submit operation
   * This function eliminates duplication between handleSave and handleSubmit
   * @param {Object} params - Operation parameters
   * @param {Array} params.students - All students
   * @param {Object} params.selectedActivity - Selected activity
   * @param {String} params.selectedTahun - Selected tahun
   * @param {Number} params.totalStudents - Total student count
   * @param {Object} params.sizeConfig - Size configuration
   * @param {Object} params.gridApi - AG-Grid API
   * @param {String} params.mode - 'save' or 'submit'
   * @returns {Promise} Redux action result
   */
  const performSaveSubmit = async ({
    students,
    selectedActivity,
    selectedTahun,
    totalStudents,
    sizeConfig,
    gridApi,
    mode  // 'save' or 'submit'
  }) => {
    // 1. Validation: Check if activity and tahun are selected
    if (!selectedActivity || !selectedTahun) {
      Swal.fire('Error', 'Please select Activity and Tahun', 'error');
      return null;
    }

    // 2. Smart filter for SAVE: Only include modified/new/deleted students
    // For SUBMIT: Validate ALL students, but only send modified ones
    console.log(`useUniformHandlers: ${mode} called`);
    const filteredStudents = filterModifiedData(students);

    // 3. Check if nothing changed (only for SAVE mode)
    if (mode === 'save' && filteredStudents.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'No Changes Detected',
        text: `No students were modified, added, or removed. Nothing to save.`,
        confirmButtonText: 'OK'
      });
      return null;
    }

    // 4. Comprehensive validation
    // CRITICAL FIX: For SUBMIT mode, validate ALL students (not just modified)
    // For SAVE mode, validate only modified students
    const studentsToValidate = mode === 'submit' ? students : filteredStudents;
    const { duplicateICs, invalidRecords } = validateUniformData(studentsToValidate, sizeConfig);
    const deletedStudents = filteredStudents.filter(s => s.isDeleted);

    // 5. Build validation error HTML
    // Note: Submit mode hides deleted students section (pass null)
    const htmlMessage = buildValidationErrorHtml(
      duplicateICs,
      mode === 'save' ? deletedStudents : null,
      invalidRecords
    );

    // 6. MODE-SPECIFIC VALIDATION AND CONFIRMATION

    if (mode === 'save') {
      // SAVE MODE: Allow incomplete uniforms with warning, but BLOCK duplicates
      return await handleSaveMode(filteredStudents, duplicateICs, deletedStudents, invalidRecords, htmlMessage, selectedActivity, selectedTahun, gridApi);
    } else {
      // SUBMIT MODE: BLOCK on any validation errors
      return await handleSubmitMode(filteredStudents, duplicateICs, invalidRecords, htmlMessage, selectedActivity, selectedTahun, totalStudents, gridApi);
    }
  };

  /**
   * Save mode logic: Allow incomplete uniforms, block duplicates
   */
  const handleSaveMode = async (filteredStudents, duplicateICs, deletedStudents, invalidRecords, htmlMessage, selectedActivity, selectedTahun, gridApi) => {
    // Add note for Save operation about incomplete uniforms
    let saveHtmlMessage = htmlMessage;
    if (invalidRecords.length > 0 && !htmlMessage.includes('info-circle')) {
      saveHtmlMessage = htmlMessage.replace(
        '</div>',
        `<div style="margin-top: 10px; padding: 8px 10px; background: #fff8e1; border-radius: 4px; border: 1px solid #ffe082;">
          <i class="fas fa-info-circle" style="color: #f57c00; margin-right: 8px; font-size: 12px;"></i>
          <span style="color: #e65100; font-size: 12px;">You can save with incomplete selections, but please complete them before submission.</span>
        </div>
        </div>`
      );
    }

    // BLOCK SAVE IF DUPLICATES EXIST
    if (duplicateICs.length > 0) {
      Swal.fire({
        icon: 'error',
        title: '<span style="color: #d32f2f; font-size: 26px; font-weight: 600;">Cannot Save - Duplicate IC Numbers</span>',
        html: saveHtmlMessage,
        confirmButtonText: '<i class="fas fa-check"></i> Understood',
        width: '950px',
        customClass: {
          confirmButton: 'swal2-confirm swal2-styled',
          popup: 'swal2-popup'
        }
      });
      return null; // BLOCK save
    }

    // ALLOW SAVE with confirmation (even if uniforms incomplete)
    let confirmed = false;

    if (deletedStudents.length > 0 || invalidRecords.length > 0) {
      const result = await Swal.fire({
        icon: 'warning',
        title: '<span style="color: #f57c00; font-size: 26px; font-weight: 600;">Review Before Saving</span>',
        html: saveHtmlMessage,
        confirmButtonText: '<i class="fas fa-check-circle"></i> Proceed with Save',
        cancelButtonText: '<i class="fas fa-times"></i> Cancel',
        showCancelButton: true,
        width: '950px',
        customClass: {
          confirmButton: 'swal2-confirm swal2-styled',
          cancelButton: 'swal2-cancel swal2-styled',
          popup: 'swal2-popup'
        }
      });
      confirmed = result.isConfirmed;
    } else {
      // No issues - simple confirmation
      const result = await Swal.fire({
        title: 'Save Uniform Data?',
        html: `
          <p>This will save your changes as a draft.</p>
          <p><strong>Changes to save:</strong></p>
          <ul style="text-align: left; display: inline-block;">
            <li>Modified: ${filteredStudents.filter(s => s.isEdited && !s.isDeleted && !s.isNewStudent).length}</li>
            <li>New: ${filteredStudents.filter(s => s.isNewStudent).length}</li>
          </ul>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Save',
        cancelButtonText: 'Cancel',
      });
      confirmed = result.isConfirmed;
    }

    if (!confirmed) return null; // User cancelled

    // Proceed with save
    const saveData = {
      activityId: selectedActivity.activityId,
      tahun: selectedTahun,
      enrolYear: selectedActivity.enrolYear.toString(),
      students: filteredStudents,
    };

    const response = await dispatch(saveUniform(saveData));

    if (response.payload) {
      const responseData = response.payload.data || response.payload;
      const validationErrors = responseData.validationErrors || [];
      const validationWarnings = responseData.validationWarnings || [];
      const studentsSkipped = responseData.studentsSkipped || 0;

      // Build HTML message for validation results
      let validationHtml = '';

      // Show errors (if any)
      if (validationErrors.length > 0) {
        validationHtml += `
          <div style="margin-top: 15px; padding: 12px; background: #ffebee; border-left: 4px solid #d32f2f; text-align: left;">
            <h4 style="margin: 0 0 10px 0; color: #c62828;">
              <i class="fas fa-times-circle"></i> ${validationErrors.length} Validation Error(s)
            </h4>
            <ul style="margin: 5px 0; padding-left: 20px;">
              ${validationErrors.map(error => `
                <li style="margin: 5px 0;">
                  <strong>${error.studentIC}</strong> - ${error.studentName}<br/>
                  <span style="color: #c62828;">${error.message}</span><br/>
                  <small style="color: #666;">${error.details}</small>
                </li>
              `).join('')}
            </ul>
          </div>
        `;
      }

      // Show warnings (if any)
      if (validationWarnings.length > 0) {
        validationHtml += `
          <div style="margin-top: 15px; padding: 12px; background: #fff8e1; border-left: 4px solid #f57c00; text-align: left;">
            <h4 style="margin: 0 0 10px 0; color: #e65100;">
              <i class="fas fa-exclamation-triangle"></i> ${validationWarnings.length} Warning(s)
            </h4>
            <ul style="margin: 5px 0; padding-left: 20px;">
              ${validationWarnings.map(warning => `
                <li style="margin: 5px 0;">
                  <strong>${warning.studentIC}</strong> - ${warning.studentName}<br/>
                  <span style="color: #f57c00;">${warning.message}</span><br/>
                  <small style="color: #666;">${warning.details}</small>
                  ${warning.conflictingSchool ? `<br/><small style="color: #888;">Previous enrollment: ${warning.conflictingSchool} / ${warning.conflictingTahun}</small>` : ''}
                </li>
              `).join('')}
            </ul>
          </div>
        `;
      }

      // Show success message with validation results
      const iconType = validationErrors.length > 0 ? 'warning' : 'success';
      const title = validationErrors.length > 0
        ? `<span style="color: #f57c00;">Saved with Validation Issues</span>`
        : 'Success';

      Swal.fire({
        icon: iconType,
        title: title,
        html: `
          <div style="text-align: left;">
            <p><strong>Save Results:</strong></p>
            <ul style="margin: 10px 0; padding-left: 20px;">
              <li>Records Saved: ${responseData.recordsSaved || 0}</li>
              <li>Records Updated: ${responseData.recordsUpdated || 0}</li>
              <li>Records Deleted: ${responseData.recordsDeleted || 0}</li>
              ${studentsSkipped > 0 ? `<li style="color: #d32f2f;">Students Skipped: ${studentsSkipped}</li>` : ''}
            </ul>
            ${validationHtml}
          </div>
        `,
        width: '700px',
        confirmButtonText: 'OK'
      });

      // REMOVE STUDENTS FROM GRID: Deleted or transferred to different Tahun
      const studentsToRemove = filteredStudents.filter(student => {
        if (student.isDeleted) {
          console.log(`useUniformHandlers: Removing deleted student: ${student.name} (${student.noKP})`);
          return true;
        }
        if (student.tahun && student.tahun !== selectedTahun) {
          console.log(`useUniformHandlers: Removing student transferred to ${student.tahun}: ${student.name} (${student.noKP})`);
          return true;
        }
        return false;
      });

      if (studentsToRemove.length > 0 && gridApi) {
        gridApi.applyTransaction({ remove: studentsToRemove });
        console.log(`useUniformHandlers: Removed ${studentsToRemove.length} students from grid`);
      }
    }

    return response;
  };

  /**
   * Submit mode logic: BLOCK on any validation errors
   */
  const handleSubmitMode = async (filteredStudents, duplicateICs, invalidRecords, htmlMessage, selectedActivity, selectedTahun, totalStudents, gridApi) => {
    // Add blocking note for Submit operation about incomplete uniforms
    let submitHtmlMessage = htmlMessage;
    if (invalidRecords.length > 0 && !htmlMessage.includes('fa-ban')) {
      submitHtmlMessage = htmlMessage.replace(
        '</div>\n        </div>',
        `<div style="margin-top: 10px; padding: 8px 10px; background: #ffebee; border-radius: 4px; border: 1px solid #ef5350;">
          <i class="fas fa-ban" style="color: #c62828; margin-right: 8px; font-size: 12px;"></i>
          <span style="color: #c62828; font-size: 12px;"><strong>Submission blocked:</strong> All uniform selections must be complete.</span>
        </div>
        </div>
        </div>`
      );
    }

    // BLOCK SUBMIT IF ANY VALIDATION ERRORS
    if (duplicateICs.length > 0 || invalidRecords.length > 0) {
      Swal.fire({
        icon: 'error',
        title: '<span style="color: #d32f2f; font-size: 26px; font-weight: 600;">Cannot Submit</span>',
        html: submitHtmlMessage,
        confirmButtonText: '<i class="fas fa-check"></i> Understood',
        width: '950px',
        customClass: {
          confirmButton: 'swal2-confirm swal2-styled',
          popup: 'swal2-popup'
        }
      });
      return null; // BLOCK submit
    }

    // All validations passed - show confirmation dialog
    const result = await Swal.fire({
      title: 'Submit Uniform Data?',
      html: `
        <div style="text-align: left;">
          <p><strong>⚠ WARNING:</strong> After submission, this data will be locked for editing.</p>
          <p><strong>Total Students:</strong> ${totalStudents}</p>
          <p><strong>Changes to submit:</strong></p>
          <ul>
            <li>Modified: ${filteredStudents.filter(s => s.isEdited && !s.isDeleted && !s.isNewStudent).length}</li>
            <li>New: ${filteredStudents.filter(s => s.isNewStudent).length}</li>
            <li>Deleted: ${filteredStudents.filter(s => s.isDeleted).length}</li>
          </ul>
          <p style="margin-top: 15px;"><strong>Are you sure you want to submit?</strong></p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Submit',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
    });

    if (!result.isConfirmed) return null; // User cancelled

    // Proceed with submit
    const submitData = {
      activityId: selectedActivity.activityId,
      tahun: selectedTahun,
      enrolYear: selectedActivity.enrolYear.toString(),
      students: filteredStudents,
    };

    const response = await dispatch(submitUniform(submitData));

    if (response.payload) {
      const responseData = response.payload.data || response.payload;
      const validationErrors = responseData.validationErrors || [];
      const validationWarnings = responseData.validationWarnings || [];
      const studentsSkipped = responseData.studentsSkipped || 0;

      // Build HTML message for validation results
      let validationHtml = '';

      // Show errors (if any)
      if (validationErrors.length > 0) {
        validationHtml += `
          <div style="margin-top: 15px; padding: 12px; background: #ffebee; border-left: 4px solid #d32f2f; text-align: left;">
            <h4 style="margin: 0 0 10px 0; color: #c62828;">
              <i class="fas fa-times-circle"></i> ${validationErrors.length} Validation Error(s)
            </h4>
            <ul style="margin: 5px 0; padding-left: 20px;">
              ${validationErrors.map(error => `
                <li style="margin: 5px 0;">
                  <strong>${error.studentIC}</strong> - ${error.studentName}<br/>
                  <span style="color: #c62828;">${error.message}</span><br/>
                  <small style="color: #666;">${error.details}</small>
                </li>
              `).join('')}
            </ul>
          </div>
        `;
      }

      // Show warnings (if any)
      if (validationWarnings.length > 0) {
        validationHtml += `
          <div style="margin-top: 15px; padding: 12px; background: #fff8e1; border-left: 4px solid #f57c00; text-align: left;">
            <h4 style="margin: 0 0 10px 0; color: #e65100;">
              <i class="fas fa-exclamation-triangle"></i> ${validationWarnings.length} Warning(s)
            </h4>
            <ul style="margin: 5px 0; padding-left: 20px;">
              ${validationWarnings.map(warning => `
                <li style="margin: 5px 0;">
                  <strong>${warning.studentIC}</strong> - ${warning.studentName}<br/>
                  <span style="color: #f57c00;">${warning.message}</span><br/>
                  <small style="color: #666;">${warning.details}</small>
                  ${warning.conflictingSchool ? `<br/><small style="color: #888;">Previous enrollment: ${warning.conflictingSchool} / ${warning.conflictingTahun}</small>` : ''}
                </li>
              `).join('')}
            </ul>
          </div>
        `;
      }

      // Show success message with validation results
      const iconType = validationErrors.length > 0 ? 'warning' : 'success';
      const title = validationErrors.length > 0
        ? `<span style="color: #f57c00;">Submitted with Validation Issues</span>`
        : 'Success';

      Swal.fire({
        icon: iconType,
        title: title,
        html: `
          <div style="text-align: left;">
            <p><strong>Submit Results:</strong></p>
            <ul style="margin: 10px 0; padding-left: 20px;">
              <li>Records Saved: ${responseData.recordsSaved || 0}</li>
              <li>Records Updated: ${responseData.recordsUpdated || 0}</li>
              <li>Records Deleted: ${responseData.recordsDeleted || 0}</li>
              ${studentsSkipped > 0 ? `<li style="color: #d32f2f;">Students Skipped: ${studentsSkipped}</li>` : ''}
            </ul>
            <p style="margin-top: 10px; color: #f57c00; font-weight: bold;">
              <i class="fas fa-lock"></i> Data is now locked for editing.
            </p>
            ${validationHtml}
          </div>
        `,
        width: '700px',
        confirmButtonText: 'OK'
      });

      // Remove deleted students from grid
      const deletedStudents = filteredStudents.filter(s => s.isDeleted);
      if (deletedStudents.length > 0 && gridApi) {
        gridApi.applyTransaction({ remove: deletedStudents });
        console.log(`useUniformHandlers: Removed ${deletedStudents.length} deleted students from grid`);
      }
    }

    return response;
  };

  /**
   * Public API: handleSave wrapper
   */
  const handleSave = (params) => {
    return performSaveSubmit({ ...params, mode: 'save' });
  };

  /**
   * Public API: handleSubmit wrapper
   */
  const handleSubmit = (params) => {
    return performSaveSubmit({ ...params, mode: 'submit' });
  };

  return {
    handleSave,
    handleSubmit,
  };
};
